<template>
  <div class="group-week-container">
    <div class="content-header">
      <div class="header-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
      </div>
      <h2>Heti csoportonkénti fogyás</h2>
      <p>Válasszon egy csoportot és szűrje az adatokat</p>
    </div>

    <!-- Show loading message or spinner while waiting for the data -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <p>Adatok betöltése...</p>
    </div>

    <!-- Display filter options -->
    <div v-if="!loading" class="filters-section">
      <div class="filters-header">
        <h3>Szűrő beállítások</h3>
        <p>Szűrje az adatokat hónap vagy dátum tartomány szerint</p>
      </div>
      
      <div class="filters-grid">
        <!-- Month filter -->
        <div class="filter-group">
          <label for="monthFilter" class="filter-label">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            Hónap
          </label>
          <select id="monthFilter" v-model="selectedMonth" class="modern-select" @change="applyMonthFilter">
            <option value="">Mind</option>
            <option v-for="n in 12" :key="n" :value="n">{{ monthEnum[n-1] }}</option>
          </select>
        </div>

        <!-- From-To Date filter -->
        <div class="filter-group">
          <label for="fromDate" class="filter-label">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12,6 12,12 16,14"/>
            </svg>
            Dátum tartomány
          </label>
          <div class="date-range">
            <input type="date" v-model="fromDate" id="fromDate" class="modern-input" @change="clearMonthFilter" placeholder="Kezdő dátum">
            <span class="date-separator">-</span>
            <input type="date" v-model="toDate" id="toDate" class="modern-input" @change="clearMonthFilter" placeholder="Vég dátum">
          </div>
        </div>
      </div>
    </div>

    <!-- Display list of groups -->
    <div v-if="!loading && groups.length > 0" class="groups-section">
      <div class="section-title">
        <h3>Válasszon egy csoportot:</h3>
        <p>{{ groups.length }} csoport elérhető</p>
      </div>
      <div class="groups-grid">
        <div 
          v-for="group in groups" 
          :key="group.id" 
          class="group-card"
          @click="fetchAttendanceData(group.id)"
        >
          <div class="group-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div class="group-content">
            <h4>{{ group.name }}</h4>
            <p>Kattintson a részletek megtekintéséhez</p>
          </div>
          <div class="group-arrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M5 12h14M12 5l7 7-7 7"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Show selected group data -->
    <div v-if="attendanceData.length > 0" class="data-section">
      <div class="data-header">
        <div class="data-title">
          <h3>Adatok a(z) {{ selectedGroup.name }} csoporthoz</h3>
          <p>{{ filteredData.length }} hét adata • {{ teachersData.length }} tanár • {{ childrenData.length }} táncos</p>
        </div>
        <button @click="closeData" class="close-data-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      
      <div class="data-table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>
                <div class="th-content">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  Hét
                </div>
              </th>
              <th>
                <div class="th-content">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                  Időszak
                </div>
              </th>
              <th>
                <div class="th-content">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="8.5" cy="7" r="4"/>
                    <line x1="20" y1="8" x2="20" y2="14"/>
                    <line x1="23" y1="11" x2="17" y2="11"/>
                  </svg>
                  Összesítve
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in filteredData" :key="entry.count_id" class="data-row">
              <td class="week-cell">{{ entry.count_id }}. hét</td>
              <td class="period-cell">{{ getWeekDateRange(entry.count_id) }}</td>
              <td class="count-cell">{{ entry.total_attended_count }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Teachers Section -->
      <div v-if="teachersData.length > 0" class="teachers-section">
        <h4>Tanárok részvételi statisztikái</h4>
        <div class="teachers-table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Tanár neve</th>
                <th>Összes részvétel</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="teacher in teachersData" :key="teacher.id">
                <td>{{ teacher.name }}</td>
                <td class="count-cell">{{ teacher.totalAttendance }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Children Section -->
      <div v-if="childrenData.length > 0" class="children-section">
        <h4>Táncosok részvételi statisztikái</h4>
        <div class="children-table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Táncos neve</th>
                <th>Összes részvétel</th>
                <th v-for="week in 53" :key="week" v-show="week <= filteredData.length">{{ week }}.h</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="child in childrenData" :key="child.id">
                <td>{{ child.name }}</td>
                <td class="count-cell">{{ child.totalAttendance }}</td>
                <td v-for="weekData in child.attendanceMonths" :key="weekData.month" :class="weekData.attended ? 'attended-cell' : 'not-attended-cell'" v-show="weekData.month <= filteredData.length">
                  {{ weekData.attended ? '✓' : '✗' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'AttendanceTable',
  data() {
    return {
      attendanceData: [], // Weekly attendance data
      teachersData: [],   // Teachers attendance data
      childrenData: [],   // Children attendance data
      filteredData: [],   // Filtered data based on selected filters
      loading: true,      // Loading state to control when to show the table or the loading message
      currentYear: new Date().getFullYear(), // Dynamically get the current year
      groups: [],         // Group list to be populated from the backend
      selectedGroup: null, // Store the selected group
      selectedMonth: '',   // Selected month filter
      fromDate: '',        // From date filter
      toDate: '',          // To date filter
      monthEnum : [
        'Január',
        'Február',
        'Március',
        'Április',
        'Május',
        'Június',
        'Július',
        'Augusztus',
        'Szeptember',
        'Oktober',
        'November',
        'December'
      ]
    };
  },
  methods: {
    async fetchGroupData() {
      try {
        const response = await fetch(this.$BACKEND_HOST + '/api/settings/groups/info');
        if (!response.ok) {
          throw new Error('Failed to fetch groups');
        }
        const data = await response.json();
        this.groups = data.groups;
        this.loading = false;
      } catch (err) {
        this.error = err.message;
        this.loading = false;
      }
    },
    fetchAttendanceData(groupId) {
      this.loading = true;
      axios.post(this.$BACKEND_HOST + '/api/getstatistics/groupweek', {
        year: this.currentYear,
        grp_id: groupId,
      })
        .then(response => {
          this.attendanceData = response.data.weekly || [];
          this.teachersData = response.data.teachers || [];
          this.childrenData = response.data.children || [];
          this.filteredData = [...this.attendanceData];  // Initially no filtering
          const selected = this.groups.find(group => group.id === groupId);
          this.selectedGroup = selected;
          this.applyFilters();  // Apply filters immediately after loading the data
        })
        .catch(error => {
          console.error('Error fetching data:', error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getWeekDateRange(weekNumber) {
      const startOfYear = new Date(this.currentYear, 0, 1);
      const dayOfWeek = startOfYear.getDay();
      const daysToAdd = (weekNumber - 1) * 7 - (dayOfWeek === 0 ? 6 : dayOfWeek - 1);
      const startDate = new Date(startOfYear);
      startDate.setDate(startDate.getDate() + daysToAdd);

      const endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);

      return `${this.formatDate(startDate)} - ${this.formatDate(endDate)}`;
    },
    formatDate(date) {
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('hu-HU');
    },
    closeData() {
      this.attendanceData = [];
      this.teachersData = [];
      this.childrenData = [];
      this.selectedGroup = null;
    },

    // Method to apply filters
    applyFilters() {
      this.filteredData = this.attendanceData.filter(entry => {
        const weekStartDate = this.getWeekDateRange(entry.count_id).split(' - ')[0]; // Get start date of the week
        const weekDate = new Date(weekStartDate);
        const monthMatches = this.selectedMonth ? weekDate.getMonth() + 1 === this.selectedMonth : true;
        const fromDateMatches = this.fromDate ? weekDate >= new Date(this.fromDate) : true;
        const toDateMatches = this.toDate ? weekDate <= new Date(this.toDate) : true;

        return monthMatches && fromDateMatches && toDateMatches;
      });
    },

    // Clears the month filter when a from-to date range is set
    clearMonthFilter() {
      if (this.fromDate || this.toDate) {
        this.selectedMonth = '';  // Clear month filter if From-To dates are set
        this.applyFilters(); // Reapply filters
      }
    },

    // Apply the month filter and clear the From-To date filter
    applyMonthFilter() {
      if (this.selectedMonth) {
        this.fromDate = '';  // Clear from date filter if month is set
        this.toDate = '';    // Clear to date filter if month is set
        this.applyFilters(); // Reapply filters
      }
    }
  },
  mounted() {
    this.fetchGroupData();
  },
  watch: {
    // Watch for changes in group selection to apply filters immediately
    selectedGroup(newGroup) {
      if (newGroup) {
        this.fetchAttendanceData(newGroup.id); // Re-fetch data when group changes
      }
    }
  }
};
</script>

<style scoped>
/* Modern Group Week Container */
.group-week-container {
  padding: 0;
  max-width: 100%;
}

/* Content Header */
.content-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--color-border);
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 12px;
  color: white;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

.content-header h2 {
  color: var(--color-heading);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.content-header p {
  color: var(--color-text-muted);
  font-size: 0.95rem;
  margin: 0;
  margin-left: auto;
}

/* Loading Container */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--color-border);
  border-top: 4px solid var(--primary-blue);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-container p {
  color: var(--color-text-muted);
  font-size: 1rem;
  margin: 0;
}

/* Filters Section */
.filters-section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  margin-bottom: 2rem;
  overflow: hidden;
}

.filters-header {
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.filters-header h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.filters-header p {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  margin: 0;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  padding: 2rem;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--color-heading);
  font-size: 0.875rem;
  font-weight: 500;
  margin: 0;
}

.filter-label svg {
  color: var(--primary-blue);
  flex-shrink: 0;
}

.modern-select, .modern-input {
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  background: var(--card-bg);
  color: var(--color-text);
  font-size: 0.95rem;
  transition: all 0.3s ease;
  outline: none;
}

.modern-select:focus, .modern-input:focus {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.modern-select:hover, .modern-input:hover {
  border-color: var(--primary-blue);
}

.date-range {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.date-separator {
  color: var(--color-text-muted);
  font-weight: 500;
  flex-shrink: 0;
}

/* Groups Section */
.groups-section {
  margin-bottom: 2rem;
}

.section-title {
  margin-bottom: 1.5rem;
}

.section-title h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.section-title p {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  margin: 0;
}

.groups-grid {
  display: grid;
  gap: 1rem;
}

.group-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
  border: 1px solid var(--color-border);
  cursor: pointer;
  transition: all 0.3s ease;
}

.group-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
  border-color: var(--primary-blue);
}

.group-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
  border-radius: 10px;
  color: white;
  flex-shrink: 0;
  box-shadow: var(--shadow-sm);
}

.group-content {
  flex: 1;
}

.group-content h4 {
  color: var(--color-heading);
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.group-content p {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.4;
}

.group-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: var(--color-background-mute);
  border-radius: 50%;
  color: var(--primary-blue);
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.group-card:hover .group-arrow {
  background: var(--primary-blue);
  color: white;
  transform: translateX(4px);
}

/* Data Section */
.data-section {
  background: var(--card-bg);
  border-radius: 16px;
  box-shadow: var(--shadow);
  border: 1px solid var(--color-border);
  overflow: hidden;
}

.data-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: var(--color-background-mute);
  border-bottom: 1px solid var(--color-border);
}

.data-title h3 {
  color: var(--color-heading);
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
}

.data-title p {
  color: var(--color-text-muted);
  font-size: 0.875rem;
  margin: 0;
}

.close-data-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  background: var(--color-background-mute);
  border: 1px solid var(--color-border);
  border-radius: 50%;
  color: var(--color-text-muted);
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-data-btn:hover {
  background: var(--error);
  color: white;
  border-color: var(--error);
  transform: scale(1.1);
}

.data-table-container {
  padding: 1.5rem 2rem;
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  background: var(--card-bg);
}

.data-table thead {
  background: var(--color-background-mute);
}

.data-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--color-heading);
  border-bottom: 2px solid var(--color-border);
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.th-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.th-content svg {
  color: var(--primary-blue);
  flex-shrink: 0;
}

.data-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--color-border);
  color: var(--color-text);
  font-size: 0.95rem;
}

.data-row:hover {
  background: var(--color-background-mute);
}

.week-cell {
  font-weight: 600;
  color: var(--primary-blue);
  text-align: center;
  width: 100px;
}

.period-cell {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
}

.count-cell {
  font-weight: 600;
  color: var(--primary-blue);
  text-align: center;
  width: 120px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .content-header p {
    margin-left: 0;
  }
  
  .filters-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 1.5rem;
  }
  
  .date-range {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .date-separator {
    display: none;
  }
  
  .group-card {
    padding: 1rem;
    gap: 0.75rem;
  }
  
  .group-icon {
    width: 40px;
    height: 40px;
  }
  
  .group-content h4 {
    font-size: 1rem;
  }
  
  .group-content p {
    font-size: 0.8rem;
  }
  
  .data-header {
    padding: 1rem 1.5rem;
  }
  
  .data-title h3 {
    font-size: 1.125rem;
  }
  
  .data-table-container {
    padding: 1rem 1.5rem;
  }
  
  .data-table th,
  .data-table td {
    padding: 0.75rem;
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .filters-header {
    padding: 1rem 1.5rem;
  }
  
  .filters-grid {
    padding: 1rem;
  }
  
  .group-card {
    padding: 0.75rem;
    gap: 0.5rem;
  }
  
  .group-icon {
    width: 35px;
    height: 35px;
  }
  
  .group-content h4 {
    font-size: 0.9rem;
  }
  
  .group-content p {
    font-size: 0.75rem;
  }
  
  .group-arrow {
    width: 25px;
    height: 25px;
  }
  
  .data-header {
    padding: 0.75rem 1rem;
  }
  
  .data-title h3 {
    font-size: 1rem;
  }
  
  .data-table-container {
    padding: 0.75rem 1rem;
  }
  
  .data-table th,
  .data-table td {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
  
  .th-content span {
    display: none;
  }
  
  .th-content svg {
    width: 16px;
    height: 16px;
  }
}

.teachers-section,
.children-section {
  margin-top: 2rem;
}

.teachers-section h4,
.children-section h4 {
  color: var(--color-heading);
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 1rem;
  margin-left: 2rem;
}

.teachers-table-container,
.children-table-container {
  padding: 0 2rem;
  overflow-x: auto;
}

.attended-cell {
  text-align: center;
  color: var(--success);
  font-weight: bold;
}

.not-attended-cell {
  text-align: center;
  color: var(--error);
  font-weight: bold;
}
</style>
